import React from 'react';
import dayjs from 'dayjs';
import { css } from '@emotion/react';
import { Link, useParams } from 'react-router-dom';
import { color } from '../../../styles';
import { IGetNursingHomeAdvice } from '@/types/api/advice/getNursingHomeAdvice';
import { ADVICE_ICON_PATH } from '@/constants/advice';
import i18n from '@/i18n/configs';
import { AdviceTextPart, buildAdviceTextParts } from '@/utils/adviceLinkUtils';

interface Props {
  advice: IGetNursingHomeAdvice;
}

const Container = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #dddddd;
  padding: 8px 0;
  min-height: 60px;
  background: ${color.white};
`;

const MessageContainer = css`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

const IconStyle = css`
  margin: 0 20px;
  width: 32px;
  min-width: 32px;
  height: 32px;
`;

const NameContainer = css`
  font-size: 14px;
`;

const MessageBody = css`
  font-size: 12px;
  color: ${color.gray};
  white-space: pre-wrap;
`;

const AdviceLink = css`
  color: ${color.green};
  text-decoration: none;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

const DateContainer = css`
  font-family: 'Oswald', sans-serif;
  font-size: 12px;
  line-height: 18px;
  color: ${color.gray};
  text-wrap: nowrap;
  text-align: right;
  margin: 0 24px;
`;

// 順関数のため、外で定義（再レンダリング防止）
const renderAdvicePart = (part: AdviceTextPart, adviceId: number, index: number) => {
  const nodeKey = `${adviceId}-part-${index}`;
  if (part.type === 'text') {
    return <React.Fragment key={nodeKey}>{part.value}</React.Fragment>;
  }

  return part.isExternal ? (
    <a key={nodeKey} css={AdviceLink} href={part.to} target='_blank' rel='noopener noreferrer'>
      {part.label}
    </a>
  ) : (
    <Link key={nodeKey} css={AdviceLink} to={part.to}>
      {part.label}
    </Link>
  );
};

export const ListItem = React.memo(({ advice }: Props) => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const isJa = i18n.language.startsWith('ja');

  const adviceBody = React.useMemo(() => {
    const parts = buildAdviceTextParts({
      advice,
      isJa,
      pathParams: {
        nursingHomeId,
        residentId: advice.residentId,
      },
    });

    return parts.map((part, index) => renderAdvicePart(part, advice.id, index));
  }, [advice, isJa, nursingHomeId]);

  // アドバイスの種類に応じてアイコンを設定
  const iconPath = ADVICE_ICON_PATH[advice.adviceType] ?? ADVICE_ICON_PATH.default;

  return (
    <div css={Container}>
      <img css={IconStyle} src={iconPath} />
      <div css={MessageContainer}>
        <div css={NameContainer}>
          {advice.lastName} {advice.firstName}
        </div>
        <div css={MessageBody}>{adviceBody}</div>
      </div>
      <div css={DateContainer}>{dayjs(advice.timestamp).format('YYYY/MM/DD HH:mm')}</div>
    </div>
  );
});
